/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Col, Row } from "react-awesome-styled-grid";
import {
  Controller,
  FormProvider,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {
  Heading,
  Input,
  Label,
  Text,
  theme,
  Box,
  Select,
  Upload,
} from "@nordcloud/gnui";
import { CloudAccountCloudProvider } from "~/generated/graphql";
import { FormGroup, TransformableInput, StepContainer } from "~/components";
import { gcpRegionsOptions } from "~/constants";
import { CopyBox } from "../CopyBox";
import { FormField } from "../formConfig";
import {
  GOOGLE_INFO_ONBOARDING,
  URL_GOOGLE_CONSOLE,
  GOOGLE_ACCOUNT_NAME_PLACEHOLDER,
  GOOGLE_ACCOUNT_DESCRIPTION_PLACEHOLDER,
  GOOGLE_ACCOUNT_ENVIRONMENT_PLACEHOLDER,
  URL_GOOGLE_SERVICE_ACCOUNTS,
  URL_GOOGLE_IAM,
  URL_GOOGLE_APIS_LIBRARY,
  GOOGLE_KEY_FILE,
  GOOGLE_REQUIRED_APIS,
  GOOGLE_PROJECT_ID_PLACEHOLDER,
} from "./constants";
import { trimTransform } from "./utils";

type Props = {
  nextStep: () => void;
};

export function AccountDetailsFormGoogle({ nextStep }: Props) {
  const formMethods = useFormContext();

  const {
    register,
    formState: { errors, isValid },
    trigger,
    handleSubmit,
    control,
  } = formMethods;

  const submit = () => {
    trigger();
    if (isValid) {
      nextStep();
    }
  };

  const fileName = useWatch({
    control,
    name: FormField.jsonFile,
  });

  const uploadPlaceholder = fileName ? fileName.name : GOOGLE_KEY_FILE;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChangeCallback: (file: File) => void
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      onChangeCallback(file);
    }
  };

  return (
    <Row justify="center">
      <Col sm={6} md={6} lg={8}>
        <Row>
          <Col>
            <Heading level={3} marginBottom="spacing05">
              Account details
            </Heading>
            <input
              type="hidden"
              id="provider"
              {...register(FormField.provider)}
              value={CloudAccountCloudProvider.Gc}
            />
            <FormGroup error={errors[FormField.displayName]}>
              <Label name="Account Name" />
              <Input
                id="displayName"
                {...register(FormField.displayName)}
                placeholder={`e.g. ${GOOGLE_ACCOUNT_NAME_PLACEHOLDER}`}
              />
            </FormGroup>
            <FormGroup error={errors[FormField.description]}>
              <Label name="Description" htmlFor="description" />
              <Input
                id="description"
                {...register(FormField.description)}
                placeholder={`e.g. ${GOOGLE_ACCOUNT_DESCRIPTION_PLACEHOLDER}`}
              />
            </FormGroup>
            <FormGroup error={errors[FormField.environment]}>
              <Label name="Environment" htmlFor="environment" />
              <Input
                id="environment"
                {...register(FormField.environment)}
                placeholder={`e.g. ${GOOGLE_ACCOUNT_ENVIRONMENT_PLACEHOLDER}`}
              />
            </FormGroup>
            <Controller
              control={control}
              name={FormField.regions}
              render={({ field: { onChange: onChangeRegions, value } }) => {
                return (
                  <FormGroup error={errors[FormField.regions]}>
                    <Label name="Select Regions" required />
                    <Select
                      isMulti
                      onChange={onChangeRegions}
                      value={value}
                      placeholder="Select Regions"
                      options={gcpRegionsOptions}
                      isError={errors[FormField.regions] != null}
                    />
                  </FormGroup>
                );
              }}
            />
            <Text color={theme.color.text.text02}>
              {GOOGLE_INFO_ONBOARDING}
            </Text>
            <FormProvider {...formMethods}>
              <form id="addAccountForm" onSubmit={handleSubmit(submit)}>
                <StepContainer counter={1}>
                  <Text>
                    Sign in to the{" "}
                    <a
                      href={URL_GOOGLE_CONSOLE}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Google Cloud Console
                    </a>
                  </Text>
                  <FormGroup error={errors[FormField.projectId]}>
                    <Label
                      htmlFor={FormField.projectId}
                      name="Provide Project ID"
                      required
                    />
                    <TransformableInput
                      name={FormField.projectId}
                      transform={trimTransform}
                      control={control}
                      placeholder={`e.g. ${GOOGLE_PROJECT_ID_PLACEHOLDER}`}
                    />
                  </FormGroup>
                </StepContainer>
                <StepContainer counter={2}>
                  <Text marginBottom={theme.spacing.spacing01}>
                    Go to the{" "}
                    <a
                      href={URL_GOOGLE_SERVICE_ACCOUNTS}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Service Accounts{" "}
                    </a>
                    and create new Service Account
                  </Text>
                </StepContainer>
                <StepContainer counter={3}>
                  <Label
                    name="Go to the Service Account details and create new Key"
                    required
                  />
                  <FormGroup error={errors[FormField.jsonFile]}>
                    <Controller
                      name={FormField.jsonFile}
                      control={control}
                      render={({ field: { onChange } }) => (
                        <Upload
                          id="keyFile"
                          placeholder={uploadPlaceholder}
                          accept=".json"
                          onChange={(e) => handleInputChange(e, onChange)}
                        />
                      )}
                    />
                  </FormGroup>
                </StepContainer>
                <StepContainer counter={4}>
                  <Text marginBottom={theme.spacing.spacing01}>
                    Go to the{" "}
                    <a href={URL_GOOGLE_IAM} target="_blank" rel="noreferrer">
                      IAM{" "}
                    </a>
                    and on the resource scope add following role to the Service
                    Account
                  </Text>
                  <Box innerSpacing="spacing00">
                    <CopyBox>Service Account Token Creator</CopyBox>
                  </Box>
                </StepContainer>
                <StepContainer counter={5}>
                  <Text marginBottom={theme.spacing.spacing01}>
                    On the project level add following role to the Service
                    Account
                  </Text>
                  <Box innerSpacing="spacing00">
                    <CopyBox>Compute Instance Admin</CopyBox>
                  </Box>
                  <Text marginBottom={theme.spacing.spacing01}>
                    Alternatively, a custom role (instead of Compute Instance
                    Admin) can be created
                  </Text>
                  <Box innerSpacing="spacing00">
                    <CopyBox>compute.instances.get</CopyBox>
                    <CopyBox>compute.instances.list</CopyBox>
                    <CopyBox>compute.instances.start</CopyBox>
                    <CopyBox>compute.instances.stop</CopyBox>
                    <CopyBox>compute.zones.list</CopyBox>
                  </Box>
                </StepContainer>
                <StepContainer counter={6}>
                  <Box innerSpacing="spacing00">
                    <Text
                      color={theme.color.text.text01}
                      marginBottom="spacing01"
                    >
                      Make sure that the following APIs are enabled on the{" "}
                      <a
                        href={URL_GOOGLE_APIS_LIBRARY}
                        target="_blank"
                        rel="noreferrer"
                      >
                        project level
                      </a>
                      :
                    </Text>
                    <ul style={{ paddingLeft: "1.25rem", margin: 0 }}>
                      {GOOGLE_REQUIRED_APIS.map((api: string) => (
                        <li
                          key={api}
                          style={{
                            listStyleType: "square",
                            marginBottom: "0.25rem",
                            color: theme.color.text.text01,
                          }}
                        >
                          <Text as="span">{api}</Text>
                        </li>
                      ))}
                    </ul>
                  </Box>
                </StepContainer>
              </form>
            </FormProvider>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
